import { buildNumber } from "@helpers/app-identity";
import { getAppVersion } from "@helpers/app-version";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { mixpanelStorage } from "@helpers/storage";
import { isWeb } from "@memorang/helpers/platform";
// import * as SegmentWeb from "@segment/analytics-next";
// import * as SegmentNative from "@segment/analytics-react-native";
import * as SentryReact from "@sentry/react";
import * as SentryNative from "@sentry/react-native";
import { Mixpanel } from "mixpanel-react-native";
import { addOneSignalEmail } from "../libs/onesignal";
import {
	AnalyticsDestinationType,
	type AnalyticsDestinationTypeKeys,
	getAnalyticsDestinations,
} from "./constants";
type UserProps = {
	userId: string;
	username: string;
	email: string;
};
const trackAutomaticEvents = false; // not compatible with expo
const useNative = false; // for expo & web
const optOutTrackingDefault = false;

type EventProperties = Record<string, string | number | boolean | undefined>;

type AnalyticsHandler = {
	init: () => void;
	trackEvent: (eventName: string, properties: EventProperties) => void;
	identifyUser: (userId: string, traits: EventProperties) => void;
	resetUser: () => void;
	setProfileProperties: (properties: Record<string, string>) => void;
	mergeProfileProperties: (name: string, value: string) => void;
	updateUserProperties: (properties: Record<string, string>) => void;
	trackPageView: (
		eventName: string,
		properties: Record<string, string>,
		userParams: Record<string, string | number | undefined>,
	) => void;
};

const createMixpanelHandler = (apiKey: string): AnalyticsHandler => {
	const client = new Mixpanel(
		apiKey,
		trackAutomaticEvents,
		useNative,
		mixpanelStorage,
	);

	return {
		init: () => client.init(optOutTrackingDefault),
		trackEvent: (eventName, properties) => client.track(eventName, properties),
		identifyUser: (userId, traits) => {
			client.identify(userId);
			client.getPeople().set(traits);
		},
		resetUser: () => client.reset(),
		setProfileProperties: (properties) => client.getPeople().set(properties),
		mergeProfileProperties: (name, value) =>
			client.getPeople().union(name, [value]),
		updateUserProperties: (properties) => client.getPeople().set(properties),
		trackPageView: (eventName, properties, userParams) => {
			client.track(eventName, properties);
			if (userParams.type) {
				client.getPeople().union("mem_pages_visited", [userParams.type]);
			}
			client.getPeople().increment("mem_pages_viewed", 1);
		},
	};
};

// const createSegmentHandler = (apiKey: string): AnalyticsHandler => {
// 	let userId: string | undefined;
// 	const client =
// 		isWeb
// 			? SegmentWeb.AnalyticsBrowser.load({ writeKey: apiKey })
// 			: SegmentNative.createClient({
// 					writeKey: apiKey,
// 					trackAppLifecycleEvents: true,
// 				});

// 	return {
// 		// biome-ignore lint/suspicious/noEmptyBlockStatements: no-op
// 		init: () => {},
// 		trackEvent: (eventName, properties) => client.track(eventName, properties),
// 		identifyUser: (id, traits) => {
// 			userId = id;
// 			client.identify(id, traits);
// 		},
// 		resetUser: () => {
// 			client.reset();
// 			userId = undefined;
// 		},
// 		setProfileProperties: (properties) => client.identify(userId, properties),
// 		// biome-ignore lint/suspicious/noEmptyBlockStatements: no-op
// 		mergeProfileProperties: () => {}, // No-op for Segment
// 		updateUserProperties: (properties) => client.identify(userId, properties),
// 		trackPageView: (eventName, properties) =>
// 			client.track(eventName, properties),
// 	};
// };

const analyticsHandlers = getAnalyticsDestinations()
	.map((destination) => {
		switch (destination.type as AnalyticsDestinationTypeKeys) {
			case AnalyticsDestinationType.Mixpanel:
				return createMixpanelHandler(destination.apiKey);
			// case AnalyticsDestinationType.Segment:
			// 	return createSegmentHandler(destination.apiKey);
			default: {
				const error = new Error(
					`Unsupported analytics type: ${destination.type}`,
				);
				captureErrorToSentry(error, {
					error: `Unsupported analytics type: ${destination.type}`,
				});
				return null;
			}
		}
	})
	.filter(
		(handler): handler is NonNullable<typeof handler> => handler !== null,
	);

export const initializeAnalytics = () => {
	for (const handler of analyticsHandlers) {
		handler.init();
	}
};

const registerUserProperties = (params: UserProps) => {
	const { userId, email, username } = params;
	for (const handler of analyticsHandlers) {
		handler.identifyUser(userId, {
			uuid: userId,
			$email: email,
			$username: username,
			mem_build_version: getAppVersion(),
			mem_build_number: buildNumber,
		});
	}

	addOneSignalEmail(email);
};
const identifyUser = ({
	userId,
	email,
	username,
	app,
}: {
	userId: string;
	email: string;
	username: string;
	app: string;
}) => {
	if (!userId || typeof userId !== "string") {
		return;
	}

	const updateServices = () => {
		for (const handler of analyticsHandlers) {
			handler.identifyUser(userId, {});
		}
		addOneSignalEmail(email);

		const sentryInstance = isWeb ? SentryReact : SentryNative;
		sentryInstance.setTag("app", app);
		sentryInstance.setUser({ id: userId, email, username });
	};

	// Other analytics destinations
	updateServices();
	registerUserProperties({ userId, email, username: username || "" });
};

const updateUserProperties = (properties: Record<string, string>) => {
	for (const handler of analyticsHandlers) {
		handler.updateUserProperties(properties);
	}
};

const resetAnalytics = () => {
	baseEventProperties = {};
	for (const handler of analyticsHandlers) {
		handler.resetUser();
	}
};

const trackLogout = () => {
	const properties = {
		build_version: getAppVersion(),
		build_number: buildNumber,
	};

	for (const handler of analyticsHandlers) {
		handler.trackEvent("mem_logout", properties);
	}
	resetAnalytics();
};

/* ======== BASE PROPERTIES ========= */

let baseEventProperties: Record<string, string | number | undefined> = {};

const setBaseEventProperties = (
	properties: Record<string, string | number | undefined>,
) => {
	baseEventProperties = properties;
};

/* ======== PAGE VIEWS ========= */

const trackPageView = (
	params: Record<string, string | number | undefined> = {},
) => {
	const properties = {
		...baseEventProperties,
		...params,
		build_version: getAppVersion(),
		...(buildNumber && { build_number: buildNumber }),
	};

	for (const handler of analyticsHandlers) {
		handler.trackPageView("mem_page_viewed", properties, params);
	}
};

const setProfileProperties = (properties: Record<string, string>) => {
	for (const handler of analyticsHandlers) {
		handler.setProfileProperties(properties);
	}
};

const trackCustomEvent = (
	params: Record<string, string | number | undefined> = {},
	userProperty?: Record<string, string>,
) => {
	const { eventName, ...rest } = params;

	const properties = {
		...baseEventProperties,
		...rest,
		build_version: getAppVersion(),
		build_number: buildNumber,
	};

	for (const handler of analyticsHandlers) {
		handler.trackEvent(eventName as string, properties);
	}

	if (userProperty) {
		setProfileProperties(userProperty);
	}
};

export {
	trackPageView,
	trackLogout,
	trackCustomEvent,
	identifyUser,
	updateUserProperties,
	setBaseEventProperties,
};
